import React from 'react';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Star, Download, Shield, Zap } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../hooks/use-toast';

const PluginCard = ({ plugin }) => {
  const navigate = useNavigate();
  const { toast } = useToast();

  // Make sure plugin has all required properties
  const safePlugin = {
    id: plugin.id,
    name: plugin.name || 'Unnamed Plugin',
    tagline: plugin.tagline || 'No description available',
    description: plugin.description || 'No description available',
    category: plugin.category || 'general',
    price: plugin.price || 0,
    rating: plugin.rating || 0,
    reviews: plugin.reviews || 0,
    badge: plugin.badge || 'Free',
    compatibility: plugin.compatibility || [],
    version: plugin.version || '1.0.0',
    fileSize: plugin.fileSize || 'Unknown',
    downloads: plugin.downloads || 0,
    lastUpdated: plugin.lastUpdated || new Date().toISOString(),
    features: plugin.features || [],
    changelog: plugin.changelog || 'No changelog available',
    demoServer: plugin.demoServer || 'No demo available',
    supportChannel: plugin.supportChannel || 'No support channel',
    downloadLink: plugin.downloadLink || null
  };

  const getBadgeColor = (badge) => {
    switch (badge) {
      case 'Premium':
        return 'bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-black';
      case 'New':
        return 'bg-gradient-to-r from-[#8B5CF6] to-[#A78BFA] text-white';
      case 'Free':
        return 'bg-gradient-to-r from-[#0EA5FF] to-[#3B82F6] text-white';
      default:
        return 'bg-gray-700 text-white';
    }
  };

  const handleButtonClick = (e) => {
    e.stopPropagation();
    if (safePlugin.price === 0 && safePlugin.downloadLink) {
      // Free plugin with download link - direct download
      window.open(safePlugin.downloadLink, '_blank');
      toast({
        title: 'Download Started!',
        description: 'Your plugin is downloading now. Check Discord for installation help.',
      });
    } else {
      // Premium plugin or no download link - go to details page
      navigate(`/plugin/${safePlugin.id}`);
    }
  };

  const handleCardClick = () => {
    navigate(`/plugin/${safePlugin.id}`);
  };

  return (
    <Card
      className="plugin-card group relative overflow-hidden cursor-pointer glass hover:glass-strong transform transition-all duration-500 hover:scale-105"
      style={{
        borderRadius: '0px',
        transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
      }}
      onClick={handleCardClick}
    >
      {/* Premium glow effect on hover */}
      <div
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
        style={{
          background: 'radial-gradient(circle at center, rgba(0, 255, 209, 0.15), transparent 70%)',
          pointerEvents: 'none',
        }}
      />
      
      {/* Shimmer effect */}
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700">
        <div 
          className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-10"
          style={{ transform: 'translateX(-100%)', animation: 'shimmer 2s infinite' }}
        />
      </div>

      <div className="p-6 relative z-10">
        {/* Badge */}
        <div className="flex justify-between items-start mb-4">
          <Badge className={`${getBadgeColor(safePlugin.badge)} px-3 py-1 text-sm font-medium border-none`}>
            {safePlugin.badge}
          </Badge>
          <div className="flex items-center gap-1 text-[#D4AF37]">
            <Star className="w-4 h-4 fill-current" />
            <span className="text-sm font-medium">{safePlugin.rating}</span>
          </div>
        </div>

        {/* Plugin Info */}
        <div className="mb-4">
          <h3
            className="text-2xl font-bold mb-2 group-hover:text-glow-blue transition-all duration-300"
            style={{ color: '#FFFFFF', fontWeight: 700, letterSpacing: '-0.02em', fontFamily: 'Orbitron, Inter, sans-serif' }}
          >
            {safePlugin.name}
          </h3>
          <p className="text-base" style={{ color: 'rgba(255, 255, 255, 0.85)', lineHeight: 1.5 }}>
            {safePlugin.tagline}
          </p>
        </div>

        {/* Stats */}
        <div className="flex items-center gap-4 mb-4 text-sm" style={{ color: '#4D4D4D' }}>
          <div className="flex items-center gap-1">
            <Download className="w-4 h-4" />
            <span>{safePlugin.downloads.toLocaleString()}</span>
          </div>
          <div className="flex items-center gap-1">
            <Shield className="w-4 h-4" />
            <span>{safePlugin.version}</span>
          </div>
        </div>

        {/* Compatibility badges */}
        <div className="flex flex-wrap gap-2 mb-4">
          {safePlugin.compatibility.slice(0, 3).map((comp, index) => (
            <span
              key={index}
              className="px-2 py-1 text-xs rounded"
              style={{
                background: 'rgba(255, 255, 255, 0.1)',
                color: 'rgba(255, 255, 255, 0.85)',
                border: '1px solid rgba(255, 255, 255, 0.25)',
              }}
            >
              {comp}
            </span>
          ))}
        </div>

        {/* Price and CTA */}
        <div className="flex items-center justify-between pt-4" style={{ borderTop: '1px solid rgba(255, 255, 255, 0.25)' }}>
          <div>
            {safePlugin.price === 0 ? (
              <span className="text-2xl font-semibold" style={{ color: '#0EA5FF' }}>
                FREE
              </span>
            ) : (
              <span className="text-2xl font-semibold" style={{ color: '#D4AF37' }}>
                ${safePlugin.price}
              </span>
            )}
          </div>
          <Button
            className="btn-primary group/btn glow-cyan relative overflow-hidden"
            style={{
              background: '#00FFD1',
              color: '#000000',
              borderRadius: '0px',
              padding: '12px 24px',
              fontSize: '16px',
              fontWeight: 700,
              border: 'none',
              transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
              fontFamily: 'Orbitron, Inter, sans-serif',
            }}
            onClick={handleButtonClick}
          >
            <span className="relative z-10">
              {safePlugin.price === 0 ? 'Free Download' : 'View Details'}
            </span>
            <Zap className="w-4 h-4 ml-2 inline relative z-10" />
          </Button>
        </div>
      </div>

      {/* Premium animated border glow */}
      <div
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
        style={{
          boxShadow: '0 0 40px rgba(0, 255, 209, 0.6), 0 0 80px rgba(0, 255, 209, 0.3)',
        }}
      />
    </Card>
  );
};

export default PluginCard;